/**
 * DataValidationTab.jsx
 * 
 * DAS-1782: Data Validation tab for Backtesting page.
 * Allows users to run validation checks on historical data tables.
 */


import React, { useState, useEffect } from 'react';
import {
  Card,
  Select,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  message,
  Spin,
  DatePicker,
  Collapse,
  Statistic,
  Row,
  Col,
  Progress,
  Descriptions,
  Empty,
  Alert,
  Tooltip,
  Modal,
  List,
  Input,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  PlayCircleOutlined,
  FileSearchOutlined,
  CopyOutlined,
  FileTextOutlined,
  HistoryOutlined,
  EyeOutlined,
  ReloadOutlined,
  DashboardOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../functions/api';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { Option } = Select;


/**
 * Quality score color based on value
 */
const getScoreColor = (score) => {
  if (score >= 90) return '#52c41a'; // Green
  if (score >= 70) return '#faad14'; // Orange
  return '#ff4d4f'; // Red
};

/**
 * Severity tag for gaps
 */
const SeverityTag = ({ severity }) => {
  const colors = {
    CRITICAL: 'red',
    WARNING: 'orange',
    INFO: 'blue',
  };
  return <Tag color={colors[severity] || 'default'}>{severity}</Tag>;
};


/**
 * Field Rules Display
 */
const FieldRulesCard = ({ rule }) => {
  if (!rule) return null;
  
  return (
    <Card size="small" title="Validation Rules" style={{ marginTop: 16 }}>
      <Descriptions column={2} size="small">
        <Descriptions.Item label="Min Value">
          {rule.min_value !== null ? rule.min_value : 'None'}
        </Descriptions.Item>
        <Descriptions.Item label="Max Value">
          {rule.max_value !== null ? rule.max_value : 'None'}
        </Descriptions.Item>
        <Descriptions.Item label="Description">
          {rule.description || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Unit">
          {rule.unit || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Allow Null">
          {rule.allow_null ? 'Yes' : 'No'}
        </Descriptions.Item>
        <Descriptions.Item label="Day-over-Day Threshold">
          {rule.day_over_day_threshold 
            ? `${(rule.day_over_day_threshold * 100).toFixed(0)}%` 
            : 'None'}
        </Descriptions.Item>
      </Descriptions>
    </Card>
  );
};


/**
 * Main Data Validation Tab Component
 */
const DataValidationTab = () => {
  // State
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [fields, setFields] = useState({});
  const [selectedField, setSelectedField] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(60, 'day'),
    dayjs(),
  ]);
  const [loading, setLoading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  
  // Consistency check state
  const [consistencyLoading, setConsistencyLoading] = useState(false);
  const [consistencyResult, setConsistencyResult] = useState(null);
  
  // Run All Fields state
  const [allFieldsLoading, setAllFieldsLoading] = useState(false);
  const [allFieldsResult, setAllFieldsResult] = useState(null);
  
  // Historical reports state
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportContent, setReportContent] = useState(null);
  
  // DAS-1875: Daily Summary Dashboard state
  const [dailySummary, setDailySummary] = useState(null);
  const [dailySummaryLoading, setDailySummaryLoading] = useState(false);
  const [showDailySummaryDetail, setShowDailySummaryDetail] = useState(false);
  
  // DAS-1905: Jira ticket creation state
  const [jiraModalOpen, setJiraModalOpen] = useState(false);
  const [jiraNote, setJiraNote] = useState('');
  const [jiraCreating, setJiraCreating] = useState(false);
  
  // DAS-1906: Select Now readiness state
  const [selectNowReadiness, setSelectNowReadiness] = useState(null);
  const [selectNowReadinessLoading, setSelectNowReadinessLoading] = useState(false);
  
  // Fetch tables, daily summary, and Select Now readiness on mount
  useEffect(() => {
    fetchTables();
    fetchDailySummary();
    fetchSelectNowReadiness();
  }, []);
  
  // Fetch fields when table changes
  useEffect(() => {
    if (selectedTable) {
      fetchFields(selectedTable);
      setSelectedField(null);
      setValidationResult(null);
    }
  }, [selectedTable]);
  
  /**
   * DAS-1875: Fetch the latest daily validation summary (from cron)
   */
  const fetchDailySummary = async () => {
    setDailySummaryLoading(true);
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_get_daily_summary',
        payload: {}
      });
      if (res.data.success && res.data.data?.found) {
        setDailySummary(res.data.data);
      } else {
        setDailySummary(null);
        // Don't show error - it's optional
        console.log('No daily summary available:', res.data.data?.error || res.data.error);
      }
    } catch (err) {
      console.error('Failed to fetch daily summary:', err);
      setDailySummary(null);
    } finally {
      setDailySummaryLoading(false);
    }
  };

  /**
   * DAS-1905: Copy report path to clipboard
   */
  const copyReportPath = () => {
    if (dailySummary?.path) {
      navigator.clipboard.writeText(dailySummary.path);
      message.success('Report path copied to clipboard');
    }
  };

  /**
   * DAS-1905: Create Jira ticket from validation report
   */
  const createJiraTicket = async () => {
    if (!dailySummary) return;
    
    setJiraCreating(true);
    try {
      // Build summary from critical issues
      const criticalCount = dailySummary.issues?.critical_count || 0;
      const warningCount = dailySummary.issues?.warning_count || 0;
      const reportSummary = `${criticalCount} critical, ${warningCount} warning issues (${dailySummary.generated_at || 'today'})`;
      
      const res = await api.post('/backtesting', {
        action: 'validation_create_jira_ticket',
        payload: {
          note: jiraNote,
          report_path: dailySummary.path || '',
          report_summary: reportSummary,
        }
      });
      
      if (res.data.success && res.data.data?.key) {
        message.success(`Created ticket ${res.data.data.key}`);
        setJiraModalOpen(false);
        setJiraNote('');
      } else {
        message.error(res.data.error || 'Failed to create ticket');
      }
    } catch (err) {
      console.error('Failed to create Jira ticket:', err);
      message.error('Failed to create Jira ticket');
    } finally {
      setJiraCreating(false);
    }
  };

  /**
   * DAS-1906: Fetch Select Now readiness status
   */
  const fetchSelectNowReadiness = async () => {
    setSelectNowReadinessLoading(true);
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_check_select_now_readiness',
        payload: {}
      });
      if (res.data.success) {
        console.log('Select Now Readiness data:', res.data.data);
        console.log('dates_checked:', res.data.data?.dates_checked);
        setSelectNowReadiness(res.data.data);
      } else {
        setSelectNowReadiness(null);
        console.log('Select Now readiness check failed:', res.data.error);
      }
    } catch (err) {
      console.error('Failed to fetch Select Now readiness:', err);
      setSelectNowReadiness(null);
    } finally {
      setSelectNowReadinessLoading(false);
    }
  };

  /**
   * Fetch available tables (via generic backtesting API)
   */
  const fetchTables = async () => {
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_list_tables',
        payload: {}
      });
      if (res.data.success) {
        setTables(res.data.data?.tables || []);
      } else {
        throw new Error(res.data.error || 'Failed to load tables');
      }
    } catch (err) {
      console.error('Failed to fetch tables:', err);
      message.error('Failed to load validation tables');
    }
  };
  
  /**
   * Fetch fields and rules for a table (via generic backtesting API)
   */
  const fetchFields = async (tableName) => {
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_list_fields',
        payload: { table: tableName }
      });
      if (res.data.success) {
        setFields(res.data.data?.fields || {});
      } else {
        throw new Error(res.data.error || 'Failed to load fields');
      }
    } catch (err) {
      console.error('Failed to fetch fields:', err);
      message.error('Failed to load fields');
    }
  };
  
  /**
   * Run validation (via generic backtesting API)
   */
  const runValidation = async () => {
    if (!selectedTable || !selectedField || !dateRange[0] || !dateRange[1]) {
      message.warning('Please select table, field, and date range');
      return;
    }
    
    setLoading(true);
    setValidationResult(null);
    
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_run',
        payload: {
          table: selectedTable,
          field: selectedField,
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
        }
      });
      
      if (!res.data.success) {
        throw new Error(res.data.error || 'Validation failed');
      }
      
      const result = res.data.data;
      setValidationResult(result);
      
      if (result.quality_score >= 90) {
        message.success(`Validation complete: Quality Score ${result.quality_score}/100`);
      } else if (result.quality_score >= 70) {
        message.warning(`Validation complete: Quality Score ${result.quality_score}/100 (needs attention)`);
      } else {
        message.error(`Validation complete: Quality Score ${result.quality_score}/100 (critical issues)`);
      }
    } catch (err) {
      console.error('Validation failed:', err);
      message.error(err.response?.data?.error || err.message || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Run cross-table consistency check (via generic backtesting API)
   */
  const runConsistencyCheck = async () => {
    setConsistencyLoading(true);
    setConsistencyResult(null);
    
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_consistency',
        payload: {}  // Uses default 60 days
      });
      
      if (!res.data.success) {
        throw new Error(res.data.error || 'Consistency check failed');
      }
      
      const result = res.data.data;
      setConsistencyResult(result);
      
      if (result.issues_found === 0) {
        message.success('No consistency issues found!');
      } else if (result.summary?.critical > 0) {
        message.error(`Found ${result.issues_found} issues (${result.summary.critical} CRITICAL)`);
      } else {
        message.warning(`Found ${result.issues_found} consistency issues`);
      }
    } catch (err) {
      console.error('Consistency check failed:', err);
      message.error(err.response?.data?.error || err.message || 'Consistency check failed');
    } finally {
      setConsistencyLoading(false);
    }
  };
  
  /**
   * Run validation on ALL fields for the selected table (via generic backtesting API)
   */
  const runAllFieldsValidation = async () => {
    if (!selectedTable || !dateRange[0] || !dateRange[1]) {
      message.warning('Please select a table and date range');
      return;
    }
    
    setAllFieldsLoading(true);
    setAllFieldsResult(null);
    setValidationResult(null);
    
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_run_all',
        payload: {
          table: selectedTable,
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
        }
      });
      
      if (!res.data.success) {
        throw new Error(res.data.error || 'Validation failed');
      }
      
      const result = res.data.data;
      setAllFieldsResult(result);
      
      const { avg_quality_score, summary } = result;
      
      if (summary.critical > 0) {
        message.error(`${summary.critical} fields have critical issues (avg score: ${avg_quality_score}/100)`);
      } else if (summary.warning > 0) {
        message.warning(`${summary.warning} fields need attention (avg score: ${avg_quality_score}/100)`);
      } else {
        message.success(`All ${result.fields_validated} fields passed! (avg score: ${avg_quality_score}/100)`);
      }
    } catch (err) {
      console.error('Run all fields failed:', err);
      message.error(err.response?.data?.error || err.message || 'Validation failed');
    } finally {
      setAllFieldsLoading(false);
    }
  };
  
  /**
   * Fetch historical validation reports (via generic backtesting API)
   */
  const fetchReports = async () => {
    setReportsLoading(true);
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_list_reports',
        payload: { limit: 30 }
      });
      if (res.data.success) {
        setReports(res.data.data?.reports || []);
      } else {
        throw new Error(res.data.error || 'Failed to load reports');
      }
    } catch (err) {
      console.error('Failed to fetch reports:', err);
      message.error('Failed to load historical reports');
    } finally {
      setReportsLoading(false);
    }
  };
  
  /**
   * View a specific report (via generic backtesting API)
   */
  const viewReport = async (filename) => {
    setSelectedReport(filename);
    try {
      const res = await api.post('/backtesting', {
        action: 'validation_get_report',
        payload: { filename }
      });
      if (res.data.success) {
        setReportContent(res.data.data?.content);
      } else {
        throw new Error(res.data.error || 'Failed to load report');
      }
    } catch (err) {
      console.error('Failed to load report:', err);
      message.error('Failed to load report content');
      setReportContent(null);
    }
  };
  
  /**
   * Outliers table columns
   */
  const outlierColumns = [
    ...(validationResult?.outliers?.outliers?.[0]?.ticker ? [{
      title: 'Ticker',
      dataIndex: 'ticker',
      key: 'ticker',
      width: 100,
    }] : []),
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 150,
      render: (v) => v !== null ? v.toLocaleString() : 'NULL',
    },
    {
      title: 'Issue',
      dataIndex: 'issue',
      key: 'issue',
      render: (issue) => <Text type="danger">{issue}</Text>,
    },
  ];
  
  /**
   * Gaps table columns
   */
  const gapColumns = [
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      key: 'start_date',
      width: 120,
    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      key: 'end_date',
      width: 120,
    },
    {
      title: 'Gap (Days)',
      dataIndex: 'gap_days',
      key: 'gap_days',
      width: 100,
      render: (days) => <Text strong>{days}</Text>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => <SeverityTag severity={severity} />,
    },
  ];
  
  /**
   * Spikes table columns (day-over-day threshold violations)
   */
  const spikeColumns = [
    {
      title: 'Ticker',
      dataIndex: 'ticker',
      key: 'ticker',
      width: 100,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 110,
    },
    {
      title: 'Prev Value',
      dataIndex: 'prev_value',
      key: 'prev_value',
      width: 120,
      render: (v) => v !== null ? v.toLocaleString() : 'N/A',
    },
    {
      title: 'Curr Value',
      dataIndex: 'curr_value',
      key: 'curr_value',
      width: 120,
      render: (v) => v !== null ? v.toLocaleString() : 'N/A',
    },
    {
      title: 'Change',
      dataIndex: 'pct_change',
      key: 'pct_change',
      width: 100,
      render: (v, record) => (
        <Text type={record.direction === 'increase' ? 'success' : 'danger'}>
          {v !== null ? `${v}%` : 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      width: 100,
      render: (dir) => (
        <Tag color={dir === 'increase' ? 'green' : 'red'}>
          {dir === 'increase' ? '↑ Up' : '↓ Down'}
        </Tag>
      ),
    },
  ];
  
  return (
    <div style={{ padding: '0' }}>
      {/* DAS-1875: Daily Summary Dashboard - Shows latest cron validation results */}
      {dailySummaryLoading ? (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin size="small" />
            <Text type="secondary" style={{ marginLeft: 8 }}>Loading daily validation summary...</Text>
          </div>
        </Card>
      ) : dailySummary ? (
        <Card 
          style={{ marginBottom: 16, borderLeft: `4px solid ${getScoreColor(dailySummary.overall?.quality_score || 0)}` }}
          title={
            <Space>
              <DashboardOutlined />
              <span>Daily Validation Summary</span>
              <Tag color={
                dailySummary.overall?.quality_score >= 90 ? 'green' :
                dailySummary.overall?.quality_score >= 70 ? 'orange' : 'red'
              }>
                {dailySummary.overall?.quality_score || 0}/100
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {dailySummary.generated_at ? `Generated: ${dailySummary.generated_at}` : ''}
              </Text>
            </Space>
          }
          extra={
            <Space>
              <Button 
                size="small" 
                icon={<EyeOutlined />}
                onClick={() => setShowDailySummaryDetail(true)}
              >
                View Report
              </Button>
              <Button 
                size="small" 
                icon={<ReloadOutlined />}
                onClick={fetchDailySummary}
              >
                Refresh
              </Button>
            </Space>
          }
          size="small"
        >
          <Row gutter={16}>
            <Col span={4}>
              <Statistic
                title="Quality Score"
                value={dailySummary.overall?.quality_score || 0}
                suffix="/100"
                valueStyle={{ 
                  color: getScoreColor(dailySummary.overall?.quality_score || 0),
                  fontSize: 24 
                }}
              />
            </Col>
            <Col span={3}>
              <Statistic
                title="Tables"
                value={dailySummary.overall?.tables_validated || 0}
              />
            </Col>
            <Col span={3}>
              <Statistic
                title="Fields"
                value={dailySummary.overall?.fields_validated || 0}
              />
            </Col>
            <Col span={3}>
              <Statistic
                title={<><ExclamationCircleOutlined style={{ color: '#ff4d4f' }} /> Critical</>}
                value={dailySummary.issues?.critical_count || 0}
                valueStyle={{ color: dailySummary.issues?.critical_count > 0 ? '#ff4d4f' : '#52c41a' }}
              />
            </Col>
            <Col span={3}>
              <Statistic
                title={<><WarningOutlined style={{ color: '#faad14' }} /> Warning</>}
                value={dailySummary.issues?.warning_count || 0}
                valueStyle={{ color: dailySummary.issues?.warning_count > 0 ? '#faad14' : '#52c41a' }}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Gaps"
                value={dailySummary.overall?.total_gaps || 0}
              />
            </Col>
            <Col span={4}>
              <Statistic
                title="Date Range"
                value={dailySummary.date_range?.end || '-'}
                valueStyle={{ fontSize: 14 }}
              />
            </Col>
          </Row>
          
          {/* Show critical fields if any */}
          {dailySummary.issues?.critical_fields?.length > 0 && (
            <div style={{ marginTop: 12, padding: 8, background: '#fff2f0', borderRadius: 4 }}>
              <Text strong style={{ color: '#ff4d4f' }}>
                🔴 Critical Fields (score &lt; 50):
              </Text>
              <div style={{ marginTop: 4 }}>
                {dailySummary.issues.critical_fields.map((f, i) => (
                  <Tag key={i} color="red" style={{ marginRight: 4, marginBottom: 4 }}>
                    {f.field} ({f.score})
                  </Tag>
                ))}
              </div>
            </div>
          )}
          
          {/* Show errors if any */}
          {dailySummary.errors?.length > 0 && (
            <div style={{ marginTop: 8, padding: 8, background: '#fffbe6', borderRadius: 4 }}>
              <Text strong style={{ color: '#d48806' }}>
                ⚠️ Validation Errors:
              </Text>
              <div style={{ marginTop: 4 }}>
                {dailySummary.errors.slice(0, 3).map((err, i) => (
                  <Tag key={i} color="warning" style={{ marginRight: 4, marginBottom: 4 }}>
                    {err}
                  </Tag>
                ))}
                {dailySummary.errors.length > 3 && (
                  <Text type="secondary">+{dailySummary.errors.length - 3} more...</Text>
                )}
              </div>
            </div>
          )}
        </Card>
      ) : null}
      
      {/* DAS-1906: Select Now Readiness Card */}
      {selectNowReadinessLoading ? (
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ textAlign: 'center', padding: 16 }}>
            <Spin size="small" />
            <Text type="secondary" style={{ marginLeft: 8 }}>Checking Select Now readiness...</Text>
          </div>
        </Card>
      ) : selectNowReadiness ? (
        <Card 
          size="small"
          style={{ 
            marginBottom: 16, 
            borderLeft: `4px solid ${selectNowReadiness.today_passes ? '#52c41a' : '#ff4d4f'}` 
          }}
          title={
            <Space>
              {selectNowReadiness.today_passes ? (
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
              ) : (
                <WarningOutlined style={{ color: '#ff4d4f' }} />
              )}
              <span>Select Now Readiness</span>
              <Tag color={selectNowReadiness.today_passes ? 'green' : 'red'}>
                {selectNowReadiness.today_passes ? '✅ Ready' : '⚠️ Date Shift Required'}
              </Tag>
            </Space>
          }
          extra={
            <Button 
              size="small" 
              icon={<ReloadOutlined />}
              onClick={fetchSelectNowReadiness}
            >
              Refresh
            </Button>
          }
        >
          <Row gutter={16}>
            <Col span={5}>
              <Statistic
                title="Today's Date"
                value={selectNowReadiness.original_date || '-'}
                valueStyle={{ fontSize: 14 }}
              />
            </Col>
            <Col span={7}>
              <Statistic
                title={selectNowReadiness.was_shifted ? 'Recommended Date' : 'Data Date'}
                value={selectNowReadiness.selected_date || '-'}
                valueStyle={{ 
                  fontSize: 14,
                  color: selectNowReadiness.was_shifted ? '#faad14' : '#52c41a'
                }}
                suffix={selectNowReadiness.was_shifted ? ` (${selectNowReadiness.days_shifted}d ago)` : ''}
              />
            </Col>
            {/* Coverage for RECOMMENDED date (the one that passes) */}
            <Col span={12}>
              <div style={{ marginBottom: 4 }}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  Coverage for {selectNowReadiness.was_shifted ? selectNowReadiness.selected_date : 'today'}:
                </Text>
              </div>
              <Row gutter={8}>
                <Col span={6}>
                  <Statistic
                    title="Overall"
                    value={selectNowReadiness.coverage?.overall?.pct || 0}
                    suffix="%"
                    valueStyle={{ 
                      fontSize: 14,
                      color: (selectNowReadiness.coverage?.overall?.pct || 0) >= 65 ? '#52c41a' : '#ff4d4f'
                    }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="US"
                    value={selectNowReadiness.coverage?.us?.pct || 0}
                    suffix="%"
                    valueStyle={{ 
                      fontSize: 14,
                      color: (selectNowReadiness.coverage?.us?.pct || 0) >= 65 ? '#52c41a' : '#ff4d4f'
                    }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="EU"
                    value={selectNowReadiness.coverage?.eu?.pct || 0}
                    suffix="%"
                    valueStyle={{ 
                      fontSize: 14,
                      color: (selectNowReadiness.coverage?.eu?.pct || 0) >= 65 ? '#52c41a' : '#ff4d4f'
                    }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Other"
                    value={selectNowReadiness.coverage?.other?.pct || 0}
                    suffix="%"
                    valueStyle={{ 
                      fontSize: 14,
                      color: (selectNowReadiness.coverage?.other?.pct || 0) >= 65 ? '#52c41a' : '#ff4d4f'
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
          
          {/* Show ALL dates checked with their status - why each failed */}
          {selectNowReadiness.was_shifted && selectNowReadiness.dates_checked?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                📅 Dates Checked ({selectNowReadiness.dates_checked.length}):
              </Text>
              <div style={{ 
                maxHeight: 200, 
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: 4
              }}>
                <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fafafa', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Date</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>Overall</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>US</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>EU</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>Other</th>
                      <th style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '1px solid #f0f0f0' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectNowReadiness.dates_checked.map((entry, idx) => {
                      const isSelected = entry.status === 'selected';
                      const isNoData = entry.status === 'no_data';
                      const rowBg = isSelected ? '#f6ffed' : (isNoData ? '#fff7e6' : '#fff2f0');
                      
                      return (
                        <tr key={idx} style={{ background: rowBg }}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            <strong>{entry.date}</strong>
                            {entry.days_back === 0 && <span style={{ color: '#666', marginLeft: 4 }}>(today)</span>}
                          </td>
                          {isNoData ? (
                            <td colSpan={4} style={{ padding: '6px 8px', textAlign: 'center', color: '#d46b08', borderBottom: '1px solid #f0f0f0' }}>
                              No data available
                            </td>
                          ) : (
                            <>
                              {['overall', 'us', 'eu', 'other'].map(region => {
                                const pct = entry.coverage?.[region]?.pct || 0;
                                const failed = pct < 65;
                                return (
                                  <td key={region} style={{ 
                                    padding: '6px 8px', 
                                    textAlign: 'center',
                                    color: failed ? '#ff4d4f' : '#52c41a',
                                    fontWeight: failed ? 'bold' : 'normal',
                                    borderBottom: '1px solid #f0f0f0'
                                  }}>
                                    {pct.toFixed(1)}%
                                    {failed && ' ✗'}
                                  </td>
                                );
                              })}
                            </>
                          )}
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                            {isSelected && <Tag color="green" style={{ margin: 0 }}>✓ Selected</Tag>}
                            {isNoData && <Tag color="orange" style={{ margin: 0 }}>No Data</Tag>}
                            {entry.status === 'insufficient' && (
                              <Tag color="red" style={{ margin: 0 }}>Failed</Tag>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {/* Show validation rules */}
          <div style={{ marginTop: 8 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              📋 Requires: ROA + P/B both non-null & non-zero, ≥65% coverage per region (US, EU, Other)
            </Text>
          </div>
        </Card>
      ) : null}
      
      {/* Daily Summary Detail Modal */}
      <Modal
        title={<Space><FileTextOutlined /> Daily Validation Report</Space>}
        open={showDailySummaryDetail}
        onCancel={() => setShowDailySummaryDetail(false)}
        footer={null}
        width={1000}
      >
        {dailySummary && (
          <>
            {/* DAS-1905: Report path with copy icon */}
            <div style={{ 
              marginBottom: 12, 
              padding: '8px 12px', 
              background: '#fafafa', 
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Space>
                <Text strong>Report Path:</Text>
                <Text code style={{ fontSize: 11 }}>{dailySummary.path || 'N/A'}</Text>
                <Tooltip title="Copy path">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CopyOutlined />} 
                    onClick={copyReportPath}
                  />
                </Tooltip>
              </Space>
              {/* DAS-1905: Create Jira Ticket button */}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setJiraModalOpen(true)}
              >
                Create Jira Ticket
              </Button>
            </div>
            <pre style={{ 
              maxHeight: '55vh', 
              overflow: 'auto', 
              background: '#f5f5f5', 
              padding: 16, 
              borderRadius: 4,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}>
              {dailySummary.raw_content}
            </pre>
          </>
        )}
      </Modal>
      
      {/* DAS-1905: Jira Ticket Creation Modal */}
      <Modal
        title={<Space><PlusOutlined /> Create Jira Ticket</Space>}
        open={jiraModalOpen}
        onCancel={() => {
          setJiraModalOpen(false);
          setJiraNote('');
        }}
        onOk={createJiraTicket}
        confirmLoading={jiraCreating}
        okText="Create Ticket"
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Create a Jira ticket for the data validation issues. The report path will be included automatically.
          </Text>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Report Path:</Text>
          <div style={{ marginTop: 4 }}>
            <Text code style={{ fontSize: 11 }}>{dailySummary?.path || 'N/A'}</Text>
          </div>
        </div>
        <div>
          <Text strong>Additional Notes (optional):</Text>
          <Input.TextArea
            value={jiraNote}
            onChange={(e) => setJiraNote(e.target.value)}
            placeholder="Add any additional context or notes for the ticket..."
            rows={4}
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>

      {/* Header with info */}
      <Alert
        message="Data Quality Validation"
        description="Select a table and field to validate data quality. Checks include coverage analysis, gap detection, and outlier identification based on defined rules."
        type="info"
        showIcon
        icon={<FileSearchOutlined />}
        style={{ marginBottom: 16 }}
      />
      
      {/* Selection Controls */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <div>
            <Text strong>Table:</Text>
            <Select
              placeholder="Select table"
              style={{ width: 250, marginLeft: 8 }}
              value={selectedTable}
              onChange={setSelectedTable}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {tables.map((t) => (
                <Option key={t} value={t}>{t}</Option>
              ))}
            </Select>
          </div>
          
          <div>
            <Text strong>Field:</Text>
            <Select
              placeholder="Select field"
              style={{ width: 200, marginLeft: 8 }}
              value={selectedField}
              onChange={setSelectedField}
              disabled={!selectedTable}
              showSearch
            >
              {Object.keys(fields).map((f) => (
                <Option key={f} value={f}>
                  <Tooltip title={fields[f]?.description || ''}>
                    {f}
                  </Tooltip>
                </Option>
              ))}
            </Select>
          </div>
          
          <div>
            <Text strong>Date Range:</Text>
            <RangePicker
              style={{ marginLeft: 8 }}
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
            />
          </div>
          
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={runValidation}
            loading={loading}
            disabled={!selectedTable || !selectedField}
          >
            Run Validation
          </Button>
          
          <Button
            type="default"
            icon={<FileSearchOutlined />}
            onClick={runAllFieldsValidation}
            loading={allFieldsLoading}
            disabled={!selectedTable}
            style={{ marginLeft: 8 }}
          >
            Run All Fields
          </Button>
          
          <div style={{ borderLeft: '1px solid #d9d9d9', height: 32, marginLeft: 8 }} />
          
          <Button
            type="default"
            icon={<FileSearchOutlined />}
            onClick={runConsistencyCheck}
            loading={consistencyLoading}
            style={{ marginLeft: 8 }}
          >
            Cross-Table Consistency Check
          </Button>
        </Space>
      </Card>
      
      {/* Field Rules (if field selected) */}
      {selectedField && fields[selectedField] && (
        <FieldRulesCard rule={fields[selectedField]} />
      )}
      
      {/* Results */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Running validation...</div>
        </div>
      )}
      
      {/* All Fields Loading */}
      {allFieldsLoading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Validating all fields for {selectedTable}...</div>
        </div>
      )}
      
      {/* All Fields Results */}
      {!allFieldsLoading && allFieldsResult && (
        <Card 
          title={`All Fields Validation: ${allFieldsResult.table}`}
          style={{ marginTop: 16, marginBottom: 16 }}
          extra={
            <Space>
              <Tag color={allFieldsResult.summary?.critical > 0 ? 'red' : allFieldsResult.summary?.warning > 0 ? 'orange' : 'green'}>
                Avg Score: {allFieldsResult.avg_quality_score}/100
              </Tag>
              <Text type="secondary">{allFieldsResult.fields_validated} fields</Text>
            </Space>
          }
        >
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Statistic
                title="OK Fields"
                value={allFieldsResult.summary?.ok || 0}
                valueStyle={{ color: '#52c41a' }}
                prefix={<CheckCircleOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Warning Fields"
                value={allFieldsResult.summary?.warning || 0}
                valueStyle={{ color: '#faad14' }}
                prefix={<WarningOutlined />}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Critical Fields"
                value={allFieldsResult.summary?.critical || 0}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<CloseCircleOutlined />}
              />
            </Col>
          </Row>
          
          <Table
            dataSource={allFieldsResult.results || []}
            rowKey="field"
            size="small"
            pagination={false}
            columns={[
              {
                title: 'Field',
                dataIndex: 'field',
                key: 'field',
                width: 200,
              },
              {
                title: 'Quality',
                dataIndex: 'quality_score',
                key: 'quality_score',
                width: 120,
                render: (score) => (
                  <Space>
                    <Progress 
                      type="circle" 
                      percent={score} 
                      width={40}
                      strokeColor={getScoreColor(score)}
                      format={() => score}
                    />
                  </Space>
                ),
                sorter: (a, b) => a.quality_score - b.quality_score,
              },
              {
                title: 'Coverage',
                dataIndex: 'coverage_percent',
                key: 'coverage_percent',
                width: 100,
                render: (v) => v ? `${v.toFixed(1)}%` : 'N/A',
              },
              {
                title: 'Outliers',
                dataIndex: 'outlier_count',
                key: 'outlier_count',
                width: 100,
                render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <Tag color="green">0</Tag>,
              },
              {
                title: 'Gaps',
                dataIndex: 'gap_count',
                key: 'gap_count',
                width: 80,
                render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <Tag color="green">0</Tag>,
              },
              {
                title: 'Spikes',
                dataIndex: 'spike_count',
                key: 'spike_count',
                width: 80,
                render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <Tag color="green">0</Tag>,
              },
              {
                title: 'Status',
                key: 'status',
                width: 100,
                render: (_, record) => {
                  if (record.error) return <Tag color="red">Error</Tag>;
                  if (record.quality_score >= 90) return <Tag color="green">OK</Tag>;
                  if (record.quality_score >= 70) return <Tag color="orange">Warning</Tag>;
                  return <Tag color="red">Critical</Tag>;
                },
              },
            ]}
          />
        </Card>
      )}
      
      {!loading && validationResult && (
        <div style={{ marginTop: 16 }}>
          {/* Summary Cards */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Quality Score"
                  value={validationResult.quality_score}
                  suffix="/ 100"
                  valueStyle={{ color: getScoreColor(validationResult.quality_score) }}
                  prefix={
                    validationResult.quality_score >= 90 ? <CheckCircleOutlined /> :
                    validationResult.quality_score >= 70 ? <WarningOutlined /> :
                    <CloseCircleOutlined />
                  }
                />
                <Progress
                  percent={validationResult.quality_score}
                  strokeColor={getScoreColor(validationResult.quality_score)}
                  showInfo={false}
                  size="small"
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Coverage"
                  value={validationResult.coverage?.coverage_percent || 0}
                  suffix="%"
                  precision={1}
                />
                <Text type="secondary">
                  {(validationResult.coverage?.non_null_rows || 0).toLocaleString()} / {(validationResult.coverage?.total_rows || 0).toLocaleString()} rows
                </Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Date Coverage"
                  value={validationResult.coverage?.date_coverage_percent || 0}
                  suffix="%"
                  precision={1}
                />
                <Text type="secondary">
                  {validationResult.coverage?.dates_with_data || 0} / {validationResult.coverage?.total_days || 0} days
                </Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic
                  title="Issues Found"
                  value={
                    (validationResult.gaps?.gap_count || 0) + 
                    (validationResult.outliers?.outlier_count || 0) +
                    (validationResult.spikes?.spike_count || 0)
                  }
                  valueStyle={{
                    color: (validationResult.gaps?.gap_count || 0) + 
                           (validationResult.outliers?.outlier_count || 0) +
                           (validationResult.spikes?.spike_count || 0) > 0 
                      ? '#faad14' : '#52c41a'
                  }}
                />
                <Text type="secondary">
                  {validationResult.gaps?.gap_count || 0} gaps, {validationResult.outliers?.outlier_count || 0} outliers
                  {validationResult.spikes?.enabled && `, ${validationResult.spikes?.spike_count || 0} spikes`}
                </Text>
              </Card>
            </Col>
          </Row>
          
          {/* Stale Data Warning */}
          {validationResult.gaps?.is_stale && (
            <Alert
              message="Stale Data Warning"
              description={`No data found in the last ${validationResult.gaps.days_since_last_data} days. Data may need to be refreshed.`}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          {/* Detailed Results */}
          <Collapse defaultActiveKey={['gaps', 'outliers']}>
            <Panel
              header={
                <Space>
                  <span>Gaps ({validationResult.gaps?.gap_count || 0})</span>
                  {validationResult.gaps?.gap_count > 0 && (
                    <Tag color="orange">Needs Attention</Tag>
                  )}
                </Space>
              }
              key="gaps"
            >
              {validationResult.gaps?.gaps?.length > 0 ? (
                <Table
                  dataSource={validationResult.gaps.gaps}
                  columns={gapColumns}
                  rowKey={(r, i) => i}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              ) : (
                <Empty description="No significant gaps found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Panel>
            
            <Panel
              header={
                <Space>
                  <span>Outliers ({validationResult.outliers?.outlier_count || 0})</span>
                  {validationResult.outliers?.outlier_count > 0 && (
                    <Tag color="red">Review Required</Tag>
                  )}
                </Space>
              }
              key="outliers"
            >
              {validationResult.outliers?.outliers?.length > 0 ? (
                <>
                  <Table
                    dataSource={validationResult.outliers.outliers}
                    columns={outlierColumns}
                    rowKey={(r, i) => i}
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                  {validationResult.outliers.outlier_count > 50 && (
                    <Text type="secondary">
                      Showing 50 of {validationResult.outliers.outlier_count.toLocaleString()} outliers
                    </Text>
                  )}
                </>
              ) : (
                <Empty description="No outliers found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </Panel>
            
            {/* Spikes Panel - only show if spike detection is enabled */}
            {validationResult.spikes?.enabled && (
              <Panel
                header={
                  <Space>
                    <span>Day-over-Day Spikes ({validationResult.spikes?.spike_count || 0})</span>
                    {validationResult.spikes?.spike_count > 0 && (
                      <Tag color="volcano">Price Movement Alert</Tag>
                    )}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Threshold: {validationResult.spikes?.threshold_pct || 0}%
                    </Text>
                  </Space>
                }
                key="spikes"
              >
                {validationResult.spikes?.spikes?.length > 0 ? (
                  <>
                    <Table
                      dataSource={validationResult.spikes.spikes}
                      columns={spikeColumns}
                      rowKey={(r, i) => i}
                      pagination={{ pageSize: 10 }}
                      size="small"
                    />
                    {validationResult.spikes.spike_count > 50 && (
                      <Text type="secondary">
                        Showing 50 of {validationResult.spikes.spike_count.toLocaleString()} spikes
                      </Text>
                    )}
                  </>
                ) : (
                  <Empty description="No significant day-over-day spikes found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Panel>
            )}
          </Collapse>
          
          {/* Report Path with Copy Button */}
          {validationResult.report_path && (
            <Card 
              size="small" 
              style={{ marginTop: 16, background: '#f6ffed', border: '1px solid #b7eb8f' }}
            >
              <Space>
                <FileTextOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                <div>
                  <Text strong style={{ color: '#389e0d' }}>Report saved:</Text>
                  <br />
                  <Text code copyable={{ text: validationResult.report_path, icon: <CopyOutlined /> }}>
                    {validationResult.report_path}
                  </Text>
                </div>
              </Space>
            </Card>
          )}
        </div>
      )}
      
      {/* Consistency Check Results */}
      {consistencyLoading && (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>Running cross-table consistency check...</div>
        </div>
      )}
      
      {!consistencyLoading && consistencyResult && (
        <div style={{ marginTop: 16 }}>
          <Card 
            title={
              <Space>
                <FileSearchOutlined />
                <span>Cross-Table Consistency Check Results</span>
                {consistencyResult.issues_found === 0 ? (
                  <Tag color="success">✓ All Clear</Tag>
                ) : consistencyResult.summary?.critical > 0 ? (
                  <Tag color="error">{consistencyResult.summary.critical} Critical</Tag>
                ) : (
                  <Tag color="warning">{consistencyResult.issues_found} Issues</Tag>
                )}
              </Space>
            }
          >
            {/* Summary Stats */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Statistic 
                  title="Date Range" 
                  value={`${consistencyResult.date_range?.start || 'N/A'} → ${consistencyResult.date_range?.end || 'N/A'}`}
                  valueStyle={{ fontSize: 14 }}
                />
              </Col>
              <Col span={4}>
                <Statistic 
                  title="🔴 Critical" 
                  value={consistencyResult.summary?.critical || 0}
                  valueStyle={{ color: consistencyResult.summary?.critical > 0 ? '#ff4d4f' : '#52c41a' }}
                />
              </Col>
              <Col span={4}>
                <Statistic 
                  title="🟠 Warning" 
                  value={consistencyResult.summary?.warning || 0}
                  valueStyle={{ color: consistencyResult.summary?.warning > 0 ? '#faad14' : '#52c41a' }}
                />
              </Col>
              <Col span={4}>
                <Statistic 
                  title="🔵 Info" 
                  value={consistencyResult.summary?.info || 0}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="Total Issues" 
                  value={consistencyResult.issues_found || 0}
                />
              </Col>
            </Row>
            
            {/* Checks Run */}
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">Checks run: </Text>
              {(consistencyResult.checks_run || []).map(check => (
                <Tag key={check} style={{ marginRight: 4 }}>{check}</Tag>
              ))}
            </div>
            
            {/* Issues Table */}
            {consistencyResult.issues && consistencyResult.issues.length > 0 ? (
              <Table
                dataSource={consistencyResult.issues}
                columns={[
                  {
                    title: 'Severity',
                    dataIndex: 'severity',
                    key: 'severity',
                    width: 100,
                    render: (sev) => (
                      <Tag color={sev === 'CRITICAL' ? 'red' : sev === 'WARNING' ? 'orange' : 'blue'}>
                        {sev}
                      </Tag>
                    ),
                    filters: [
                      { text: 'Critical', value: 'CRITICAL' },
                      { text: 'Warning', value: 'WARNING' },
                      { text: 'Info', value: 'INFO' },
                    ],
                    onFilter: (value, record) => record.severity === value,
                  },
                  {
                    title: 'Check',
                    dataIndex: 'check',
                    key: 'check',
                    width: 150,
                    render: (check) => check?.replace(/_/g, ' '),
                  },
                  {
                    title: 'Ticker',
                    dataIndex: 'ticker',
                    key: 'ticker',
                    width: 80,
                  },
                  {
                    title: 'Date',
                    dataIndex: 'date',
                    key: 'date',
                    width: 110,
                  },
                  {
                    title: 'Message',
                    dataIndex: 'message',
                    key: 'message',
                    width: 180,
                  },
                  {
                    title: 'Details',
                    dataIndex: 'details',
                    key: 'details',
                    render: (details) => (
                      <Tooltip title={JSON.stringify(details, null, 2)}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {details ? Object.entries(details).slice(0, 2).map(([k, v]) => 
                            `${k}: ${typeof v === 'number' ? v.toFixed(2) : v}`
                          ).join(', ') : '-'}
                        </Text>
                      </Tooltip>
                    ),
                  },
                ]}
                rowKey={(r, i) => i}
                pagination={{ pageSize: 10 }}
                size="small"
              />
            ) : (
              <Empty 
                description="No consistency issues found! ✓" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
            
            {/* Report Path */}
            {consistencyResult.report_path && (
              <div style={{ marginTop: 16, padding: 12, background: '#f6ffed', borderRadius: 4, border: '1px solid #b7eb8f' }}>
                <Space>
                  <FileTextOutlined style={{ color: '#52c41a' }} />
                  <Text strong style={{ color: '#389e0d' }}>Report:</Text>
                  <Text code copyable={{ text: consistencyResult.report_path, icon: <CopyOutlined /> }}>
                    {consistencyResult.report_path}
                  </Text>
                </Space>
              </div>
            )}
          </Card>
        </div>
      )}
      
      {/* Historical Reports Section */}
      <Card 
        title={
          <Space>
            <HistoryOutlined />
            <span>Historical Reports</span>
          </Space>
        }
        style={{ marginTop: 16 }}
        extra={
          <Button 
            size="small" 
            onClick={fetchReports} 
            loading={reportsLoading}
            icon={<HistoryOutlined />}
          >
            Load Reports
          </Button>
        }
      >
        {reports.length > 0 ? (
          <Table
            dataSource={reports}
            size="small"
            pagination={{ pageSize: 8 }}
            rowKey="filename"
            columns={[
              {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                width: 100,
                render: (type) => (
                  <Tag color={type === 'validation' ? 'blue' : type === 'consistency' ? 'purple' : 'cyan'}>
                    {type}
                  </Tag>
                ),
                filters: [
                  { text: 'Validation', value: 'validation' },
                  { text: 'Consistency', value: 'consistency' },
                  { text: 'Summary', value: 'summary' },
                ],
                onFilter: (value, record) => record.type === value,
              },
              {
                title: 'Table',
                dataIndex: 'table',
                key: 'table',
                width: 180,
              },
              {
                title: 'Field',
                dataIndex: 'field',
                key: 'field',
                width: 150,
              },
              {
                title: 'Date Range',
                key: 'dates',
                width: 180,
                render: (_, record) => (
                  record.start_date && record.end_date 
                    ? `${record.start_date} → ${record.end_date}`
                    : '-'
                ),
              },
              {
                title: 'Actions',
                key: 'actions',
                width: 80,
                render: (_, record) => (
                  <Button 
                    size="small" 
                    type="link" 
                    icon={<EyeOutlined />}
                    onClick={() => viewReport(record.filename)}
                  >
                    View
                  </Button>
                ),
              },
            ]}
          />
        ) : (
          <Empty 
            description="Click 'Load Reports' to view historical validation reports" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>
      
      {/* Report Viewer Modal */}
      <Modal
        title={<Space><FileTextOutlined /> {selectedReport}</Space>}
        open={!!selectedReport && !!reportContent}
        onCancel={() => { setSelectedReport(null); setReportContent(null); }}
        footer={null}
        width={900}
      >
        <pre style={{ 
          maxHeight: '60vh', 
          overflow: 'auto', 
          background: '#f5f5f5', 
          padding: 16, 
          borderRadius: 4,
          fontSize: 12,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {reportContent}
        </pre>
      </Modal>
      
      {/* Empty state */}
      {!loading && !validationResult && !allFieldsResult && !consistencyLoading && !consistencyResult && (
        <Empty
          description="Select a table and field, then click 'Run Validation' to check data quality, or run a Cross-Table Consistency Check"
          style={{ marginTop: 48 }}
        />
      )}
    </div>
  );
};

export default DataValidationTab;

